import React, { useState, Component } from 'react';
import { Project, Block, BlockType } from '../../../lib/types';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Input } from '../../../components/ui/Input';
import { PlusIcon, GripVerticalIcon, Trash2Icon, CopyIcon, SettingsIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { getBlockEditor, getDefaultContent } from '../blocks/BlockEditorRegistry';
interface BuilderTabProps {
  project: Project;
  updateField: (field: keyof Project, value: any) => void;
}
const availableBlocks: {
  type: BlockType;
  label: string;
  desc: string;
  category: string;
}[] = [{
  type: 'hero',
  label: 'Hero Section',
  desc: 'Large title and cover image',
  category: 'Layout'
}, {
  type: 'rich-text',
  label: 'Rich Text',
  desc: 'Standard text content with formatting',
  category: 'Content'
}, {
  type: 'two-column',
  label: 'Two Column',
  desc: 'Text and image side by side',
  category: 'Layout'
}, {
  type: 'image',
  label: 'Single Image',
  desc: 'Full width or container width image',
  category: 'Media'
}, {
  type: 'gallery',
  label: 'Image Gallery',
  desc: 'Grid of multiple images',
  category: 'Media'
}, {
  type: 'video',
  label: 'Video Embed',
  desc: 'YouTube, Vimeo or MP4',
  category: 'Media'
}, {
  type: 'metrics',
  label: 'Metrics Grid',
  desc: 'Highlight key numbers and stats',
  category: 'Data'
}, {
  type: 'testimonial',
  label: 'Testimonial',
  desc: 'Large quote from client/user',
  category: 'Content'
}, {
  type: 'timeline',
  label: 'Timeline',
  desc: 'Process steps or history',
  category: 'Data'
}, {
  type: 'feature-highlights',
  label: 'Feature Highlights',
  desc: 'Grid of features with icons',
  category: 'Content'
}, {
  type: 'before-after',
  label: 'Before & After',
  desc: 'Compare two images',
  category: 'Media'
}, {
  type: 'cta',
  label: 'Call to Action',
  desc: 'Button and heading to drive action',
  category: 'Content'
}, {
  type: 'divider',
  label: 'Divider',
  desc: 'Spacing and visual separation',
  category: 'Layout'
}, {
  type: 'design-system',
  label: 'Design System',
  desc: 'Showcase typography and colors',
  category: 'Research'
}, {
  type: 'wireframes',
  label: 'Wireframes',
  desc: 'Display early stage designs',
  category: 'Research'
}, {
  type: 'research',
  label: 'Research Insights',
  desc: 'Key findings from user research',
  category: 'Research'
}, {
  type: 'competitor',
  label: 'Competitor Analysis',
  desc: 'Compare against competitors',
  category: 'Research'
}, {
  type: 'user-flow',
  label: 'User Flow',
  desc: 'Diagram and steps',
  category: 'Research'
}, {
  type: 'mockups',
  label: 'Final Mockups',
  desc: 'Showcase final designs',
  category: 'Media'
}];
export function BuilderTab({
  project,
  updateField
}: BuilderTabProps) {
  const [expandedBlock, setExpandedBlock] = useState<string | null>(null);
  const [showSettingsFor, setShowSettingsFor] = useState<string | null>(null);
  const addBlock = (type: BlockType) => {
    const newBlock: Block = {
      id: Math.random().toString(36).substr(2, 9),
      type,
      content: getDefaultContent(type),
      settings: {
        paddingTop: 'medium',
        paddingBottom: 'medium',
        backgroundColor: 'transparent',
        maxWidth: 'default',
        alignment: 'left'
      }
    };
    updateField('blocks', [...project.blocks, newBlock]);
    setExpandedBlock(newBlock.id);
  };
  const removeBlock = (id: string) => updateField('blocks', project.blocks.filter(b => b.id !== id));
  const duplicateBlock = (block: Block) => {
    const newBlock = {
      ...block,
      id: Math.random().toString(36).substr(2, 9)
    };
    const index = project.blocks.findIndex(b => b.id === block.id);
    const newBlocks = [...project.blocks];
    newBlocks.splice(index + 1, 0, newBlock);
    updateField('blocks', newBlocks);
  };
  const moveBlock = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === project.blocks.length - 1) return;
    const newBlocks = [...project.blocks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];
    updateField('blocks', newBlocks);
  };
  const updateBlockContent = (id: string, newContent: any) => {
    updateField('blocks', project.blocks.map(b => b.id === id ? {
      ...b,
      content: newContent
    } : b));
  };
  const updateBlockSettings = (id: string, key: string, value: any) => {
    updateField('blocks', project.blocks.map(b => b.id === id ? {
      ...b,
      settings: {
        ...b.settings,
        [key]: value
      }
    } : b));
  };
  return <div className="flex gap-8 h-full pb-24" data-id="element-939">
      {/* Canvas Area */}
      <div className="flex-1 space-y-4" data-id="element-940">
        <div className="border-b border-border pb-2 mb-6" data-id="element-941">
          <h2 className="font-display text-2xl font-bold" data-id="element-942">Page Builder</h2>
          <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-943">
            Construct your custom case study layout using blocks.
          </p>
        </div>

        {project.blocks.length === 0 ? <div className="border-2 border-dashed border-border p-12 text-center bg-card" data-id="element-944">
            <h3 className="font-display text-xl font-bold mb-2" data-id="element-945">
              No blocks yet
            </h3>
            <p className="font-mono text-sm text-muted-foreground mb-6" data-id="element-946">
              Add a block from the sidebar to start building your page.
            </p>
          </div> : <div className="space-y-4" data-id="element-947">
            {project.blocks.map((block, index) => {
          const EditorComponent = getBlockEditor(block.type);
          return <div key={block.id} className="border border-border bg-card overflow-hidden" data-id="element-948">
                  {/* Block Header */}
                  <div className="flex items-center justify-between p-3 bg-secondary/30 border-b border-border" data-id="element-949">
                    <div className="flex items-center gap-3" data-id="element-950">
                      <div className="cursor-grab text-muted-foreground hover:text-foreground" data-id="element-951">
                        <GripVerticalIcon className="w-4 h-4" data-id="element-952" />
                      </div>
                      <span className="font-mono text-xs uppercase tracking-widest font-bold" data-id="element-953">
                        {availableBlocks.find(b => b.type === block.type)?.label || block.type}
                      </span>
                    </div>

                    <div className="flex items-center gap-1" data-id="element-954">
                      <Button variant="ghost" size="sm" className="px-2 h-8" onClick={() => moveBlock(index, 'up')} disabled={index === 0} data-id="element-955">
                        <ChevronUpIcon className="w-4 h-4" data-id="element-956" />
                      </Button>
                      <Button variant="ghost" size="sm" className="px-2 h-8" onClick={() => moveBlock(index, 'down')} disabled={index === project.blocks.length - 1} data-id="element-957">
                        <ChevronDownIcon className="w-4 h-4" data-id="element-958" />
                      </Button>
                      <div className="w-px h-4 bg-border mx-1" data-id="element-959" />
                      <Button variant="ghost" size="sm" className="px-2 h-8" onClick={() => duplicateBlock(block)} data-id="element-960">
                        <CopyIcon className="w-4 h-4" data-id="element-961" />
                      </Button>
                      <Button variant="ghost" size="sm" className={`px-2 h-8 ${showSettingsFor === block.id ? 'text-primary' : ''}`} onClick={() => setShowSettingsFor(showSettingsFor === block.id ? null : block.id)} data-id="element-962">
                        <SettingsIcon className="w-4 h-4" data-id="element-963" />
                      </Button>
                      <Button variant="ghost" size="sm" className="px-2 h-8 text-destructive hover:text-destructive" onClick={() => removeBlock(block.id)} data-id="element-964">
                        <Trash2Icon className="w-4 h-4" data-id="element-965" />
                      </Button>
                      <Button variant="ghost" size="sm" className="px-2 h-8 ml-2 bg-secondary" onClick={() => setExpandedBlock(expandedBlock === block.id ? null : block.id)} data-id="element-966">
                        {expandedBlock === block.id ? 'Collapse' : 'Edit'}
                      </Button>
                    </div>
                  </div>

                  {/* Block Settings Panel */}
                  {showSettingsFor === block.id && <div className="p-4 bg-secondary/10 border-b border-border grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-967">
                      <Select label="Padding Top" value={block.settings?.paddingTop || 'medium'} onChange={e => updateBlockSettings(block.id, 'paddingTop', e.target.value)} options={[{
                label: 'None',
                value: 'none'
              }, {
                label: 'Small',
                value: 'small'
              }, {
                label: 'Medium',
                value: 'medium'
              }, {
                label: 'Large',
                value: 'large'
              }]} data-id="element-968" />
                      <Select label="Padding Bottom" value={block.settings?.paddingBottom || 'medium'} onChange={e => updateBlockSettings(block.id, 'paddingBottom', e.target.value)} options={[{
                label: 'None',
                value: 'none'
              }, {
                label: 'Small',
                value: 'small'
              }, {
                label: 'Medium',
                value: 'medium'
              }, {
                label: 'Large',
                value: 'large'
              }]} data-id="element-969" />
                      <Select label="Max Width" value={block.settings?.maxWidth || 'default'} onChange={e => updateBlockSettings(block.id, 'maxWidth', e.target.value)} options={[{
                label: 'Narrow',
                value: 'narrow'
              }, {
                label: 'Default',
                value: 'default'
              }, {
                label: 'Full Width',
                value: 'full'
              }]} data-id="element-970" />
                      <div data-id="element-971">
                        <label className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-1.5 block" data-id="element-972">
                          Background Color
                        </label>
                        <div className="flex gap-2" data-id="element-973">
                          <input type="color" value={block.settings?.backgroundColor?.startsWith('hsl') ? '#0f0f0f' : block.settings?.backgroundColor || '#000000'} onChange={e => updateBlockSettings(block.id, 'backgroundColor', e.target.value)} className="w-10 h-10 p-1 bg-background border border-border cursor-pointer" data-id="element-974" />
                          <Input value={block.settings?.backgroundColor || 'transparent'} onChange={e => updateBlockSettings(block.id, 'backgroundColor', e.target.value)} className="flex-1" data-id="element-975" />
                        </div>
                      </div>
                    </div>}

                  {/* Block Editor Content */}
                  {expandedBlock === block.id && <div className="p-6 bg-background" data-id="element-976">
                      <EditorComponent content={block.content} onChange={(newContent: any) => updateBlockContent(block.id, newContent)} data-id="element-977" />
                    </div>}
                </div>;
        })}
          </div>}
      </div>

      {/* Block Library Sidebar */}
      <div className="w-80 shrink-0" data-id="element-978">
        <div className="sticky top-0 bg-card border border-border p-4" data-id="element-979">
          <h3 className="font-mono text-xs uppercase tracking-widest font-bold mb-4" data-id="element-980">
            Add Block
          </h3>
          <div className="space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto pr-2" data-id="element-981">
            {['Layout', 'Content', 'Media', 'Data', 'Research'].map(category => <div key={category} data-id="element-982">
                  <h4 className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest mb-2" data-id="element-983">
                    {category}
                  </h4>
                  <div className="space-y-2" data-id="element-984">
                    {availableBlocks.filter(b => b.category === category).map(block => <button key={block.type} onClick={() => addBlock(block.type)} className="w-full text-left p-3 border border-border hover:border-primary hover:bg-primary/5 transition-colors group" data-id="element-985">
                          <div className="flex items-center justify-between mb-1" data-id="element-986">
                            <span className="font-mono text-xs font-bold uppercase tracking-widest group-hover:text-primary transition-colors" data-id="element-987">
                              {block.label}
                            </span>
                            <PlusIcon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" data-id="element-988" />
                          </div>
                          <p className="font-mono text-[10px] text-muted-foreground leading-relaxed" data-id="element-989">
                            {block.desc}
                          </p>
                        </button>)}
                  </div>
                </div>)}
          </div>
        </div>
      </div>
    </div>;
}